/**
 * Layout helpers for the order pages (docs/features/order-to-invoice/
 * 16_frontend_followup_spec.md §2): a delivery line's destination and part
 * progress, the Now tab's badge, a recording's defaults, and a refused step
 * bound to the input it names.
 *
 * Headless: no styling, no DOM. Every label and sentence the pages show
 * (statuses, health, routes, services, pricing, who placed it, the rail's
 * phrases, every Now row) is the backend's, sent on the view; this only lays
 * out the structured fields that came back.
 */

import { ApiError } from "../data/api/errors";
import {
  OrderStepNotOfferedError,
  UnsafeActionUrlError,
} from "../data/order-to-invoice-actions";
import type {
  DeliveryDestination,
  DeliveryLineDespatchView,
  DeliveryLineView,
  NowItem,
} from "../data/order-to-invoice-types";
import { extractFieldErrors } from "../data/validation";
import { currentTime } from "./current-time";

/** "3,000 of 5,000" while a line is part done, or part despatched; null otherwise. */
export function deliveryLineProgress(line: DeliveryLineView): string | null {
  if (line.status === "part_despatched" && line.despatched_quantity)
    return `${line.despatched_quantity.toLocaleString("en-GB")} of ${line.quantity.toLocaleString("en-GB")}`;
  if (line.completed_quantity <= 0 || line.outstanding_quantity <= 0)
    return null;
  return `${line.completed_quantity.toLocaleString("en-GB")} of ${line.quantity.toLocaleString("en-GB")}`;
}

/**
 * The delivery line as its Record delivered / Record received dialog offers
 * it: no more than has been despatched to it and not yet arrived, where its
 * despatches say how much went there (doc 102 §4.3).
 */
export function recordableDeliveryLine(
  line: DeliveryLineView,
): DeliveryLineView {
  if (
    line.despatched_quantity === null ||
    line.despatched_quantity === undefined
  )
    return line;
  const arriving = Math.max(
    line.despatched_quantity - line.completed_quantity,
    0,
  );
  return arriving > 0 && arriving < line.outstanding_quantity
    ? { ...line, outstanding_quantity: arriving }
    : line;
}

/** The delivery line's latest despatch (doc 102 §10.1), or null before any. */
export function latestDespatch(
  line: DeliveryLineView,
): DeliveryLineDespatchView | null {
  const despatches = line.despatches ?? [];
  return despatches.reduce<DeliveryLineDespatchView | null>(
    (latest, d) =>
      latest === null || d.despatched_on >= latest.despatched_on ? d : latest,
    null,
  );
}

/** A despatch's carrier and tracking number: "DX 1234567891"; null when neither was recorded. */
export function despatchCarrierAndTracking(
  despatch: DeliveryLineDespatchView,
): string | null {
  return (
    [despatch.carrier_label, despatch.tracking_number]
      .filter(Boolean)
      .join(" ") || null
  );
}

/** Where it goes, in a few words: "Acme Bristol", "Kestrel Swindon · SKU-FLY-A5". */
export function deliveryDestinationShort(d: DeliveryDestination): string {
  if (d.kind === "warehouse") {
    const name = d.warehouse_name ?? "Warehouse";
    return d.stock_item_ref ? `${name} · ${d.stock_item_ref}` : name;
  }
  return d.name ?? d.address_ref;
}

/** Where it goes, in full: the name and address, and who runs a warehouse. */
export function deliveryDestinationLabel(d: DeliveryDestination): string {
  if (d.kind === "address") {
    return [d.name ?? d.address_ref, ...d.address_lines].join(", ");
  }
  const name = d.warehouse_name ?? "Warehouse";
  const where = d.operated_by_name
    ? `${name} (run by ${d.operated_by_name})`
    : `${name} (our warehouse)`;
  const full = [where, ...d.address_lines].join(", ");
  return d.stock_item_ref ? `${full}, into ${d.stock_item_ref}` : full;
}

/** "A Bristol": a version's label and its name. */
export function versionLabelOf(v: {
  label: string | null;
  name: string | null;
}): string {
  return [v.label, v.name].filter(Boolean).join(" ") || "—";
}

/**
 * The Now tab's count badge: the rows to act on (to do, or needing
 * attention), red when any is late, amber otherwise; null when there are none.
 */
export function nowBadge(
  items: Array<Pick<NowItem, "tone" | "is_late">>,
): { count: number; tone: "amber" | "red" } | null {
  const acting = items.filter((i) => i.tone === "do" || i.tone === "attention");
  if (acting.length === 0) return null;
  return {
    count: acting.length,
    tone: acting.some((i) => i.is_late) ? "red" : "amber",
  };
}

/** Today's date in Europe/London, "2026-10-03": a recording's default date. */
export function londonTodayIso(now: Date = currentTime()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/**
 * The inputs a refused step on an order can name (api-validation.md). Every
 * `applies_to…` field (`applies_to.line_refs.1`, `applies_to.delivery_line_id`)
 * is the Add extra dialog's one "For" input; every `activities…` field its
 * activities group; each `parameters.{key}` the input for that parameter;
 * each `versions.{i}.label` / `.name` Name versions' input for that version;
 * Record placed's `supplier_reference` and each `lines.{i}.confirmed_cost_pence`
 * / `.confirmed_vat_pence` the placement page's inputs for that line.
 */
export type OrderStepField =
  | "reason"
  | `versions.${number}.label`
  | `versions.${number}.name`
  | "quantity"
  | "delivered_on"
  | "received_on"
  | "service_kind"
  | "activities"
  | `parameters.${string}`
  | "applies_to"
  | "price_pence"
  | "agreement_note"
  | "project_no"
  | "new_project_title"
  | "spoken_to"
  | "note"
  // Book with DPD / DX (courier chain plan 01): the service, each parcel's weight, the label's contents.
  | "service_code"
  | `parcels.${number}.weight_kg`
  | "contents"
  // The artwork steps (doc 62 §5.4's refusals, doc 60's tiers, intake's upload).
  | "file"
  // The page tags of one file sent for every part (doc 87): a page tagged twice, left nowhere or out of range.
  | "assignment.pages"
  | "accepted_finding_ids"
  | "reason_code"
  | "comment"
  | "tier"
  | "expected_on"
  | "payment_method"
  | "person_id"
  | "order_contact_person_id"
  | "customer_order_number"
  | "return_url"
  | "vat_treatment"
  | "purchase_order_line_ids"
  | "tharstern_job_no"
  | "supplier_reference"
  | "despatched_on"
  | "carrier"
  | "carrier_name"
  | "tracking_reference"
  | "tracking_number"
  | `items.${number}.quantity`
  | `lines.${number}.destinations`
  | `lines.${number}.destinations.${number}.quantity`
  | `lines.${number}.confirmed_cost_pence`
  | `lines.${number}.confirmed_vat_pence`
  | `lines.${number}.is_confirmed`
  | "amount_pence"
  | "bank_reference"
  | "lines"
  | `lines.${number}.net_pence`
  | `lines.${number}.invoice_line_id`
  | "refund"
  | "bank_refund.refunded_on"
  | "bank_refund.reference"
  // Cancel the rest (doc 98): the split across addresses and the amount agreed with the supplier.
  | "shares"
  | `shares.${number}.quantity`
  | "agreed_amount_pence"
  | "agreed_amount_reason"
  // A bought service (supplier interface spec 12): Add extra's and Change supplier's supplier (by its SUP- number)
  // and cost; Log contact's supplier.
  | "supplier_no"
  | "supplier_id"
  | "cost_pence";

const STEP_FIELDS: ReadonlySet<string> = new Set<OrderStepField>([
  "reason",
  "quantity",
  "delivered_on",
  "received_on",
  "service_kind",
  "activities",
  "applies_to",
  "price_pence",
  "agreement_note",
  "project_no",
  "new_project_title",
  "spoken_to",
  "note",
  "service_code",
  "contents",
  "file",
  "assignment.pages",
  "accepted_finding_ids",
  "reason_code",
  "comment",
  "tier",
  "expected_on",
  "payment_method",
  "person_id",
  "order_contact_person_id",
  "customer_order_number",
  "return_url",
  "vat_treatment",
  "purchase_order_line_ids",
  "tharstern_job_no",
  "supplier_reference",
  "amount_pence",
  "bank_reference",
  "lines",
  "refund",
  "bank_refund.refunded_on",
  "bank_refund.reference",
  "despatched_on",
  "carrier",
  "carrier_name",
  "tracking_reference",
  "tracking_number",
  "shares",
  "agreed_amount_pence",
  "agreed_amount_reason",
  "supplier_no",
  "supplier_id",
  "cost_pence",
]);

/**
 * The input a refused field belongs to: `applies_to.line_refs.1` is the "For" input,
 * `activities.2` the activities group, and each `parameters.{key}` its own input.
 */
function stepFieldOf(field: string): OrderStepField | null {
  if (field.startsWith("parameters.")) return field as OrderStepField;
  if (/^versions\.\d+\.(label|name)$/.test(field))
    return field as OrderStepField;
  if (/^lines\.\d+\.(confirmed_(cost|vat)_pence|is_confirmed)$/.test(field))
    return field as OrderStepField;
  if (/^lines\.\d+\.(net_pence|invoice_line_id)$/.test(field))
    return field as OrderStepField;
  if (/^items\.\d+\.quantity$/.test(field)) return field as OrderStepField;
  if (/^parcels\.\d+\.weight_kg$/.test(field)) return field as OrderStepField;
  if (/^lines\.\d+\.destinations(\.\d+\.quantity)?$/.test(field))
    return field as OrderStepField;
  if (/^shares\.\d+\.quantity$/.test(field)) return field as OrderStepField;
  const input = field.startsWith("applies_to")
    ? "applies_to"
    : field.startsWith("activities")
      ? "activities"
      : field;
  return STEP_FIELDS.has(input) ? (input as OrderStepField) : null;
}

export interface OrderStepFailure {
  /** The banner: a count when inputs are highlighted, else the server's own words. */
  summary: string;
  /** The backend's message for each input it refused, to show beside that input. */
  fieldErrors: Partial<Record<OrderStepField, string>>;
}

/**
 * What to show when a step on an order or a purchase order is refused. A
 * refusal on one input (a 400 with field errors) is bound to that input and
 * summarised by count; anything else (a 409 such as "not despatched yet", a
 * step no longer offered) is the server's own message, verbatim.
 */
export function orderStepFailure(err: unknown): OrderStepFailure {
  const fieldErrors: OrderStepFailure["fieldErrors"] = {};
  const unbound: string[] = [];
  for (const e of extractFieldErrors(err)) {
    const input = stepFieldOf(e.field);
    if (input) fieldErrors[input] ??= e.message;
    else unbound.push(e.message);
  }
  const count = Object.keys(fieldErrors).length;
  const counted =
    count === 0
      ? null
      : count === 1
        ? "One field needs correcting."
        : `${count} fields need correcting.`;
  if (counted || unbound.length > 0) {
    return {
      summary: [...unbound, counted].filter(Boolean).join(" "),
      fieldErrors,
    };
  }
  return { summary: serverMessage(err), fieldErrors };
}

function serverMessage(err: unknown): string {
  if (
    err instanceof OrderStepNotOfferedError ||
    err instanceof UnsafeActionUrlError
  )
    return err.message;
  if (err instanceof ApiError) {
    const detail = (err.body as { detail?: unknown } | null)?.detail;
    if (typeof detail === "string") return detail;
    if (
      detail &&
      typeof detail === "object" &&
      typeof (detail as { message?: unknown }).message === "string"
    )
      return (detail as { message: string }).message;
  }
  return err instanceof Error
    ? err.message
    : "Could not record that — try again shortly.";
}
